import React, { useState } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Box,
} from "@mui/material";
import { Visibility, VisibilityOff, LockOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";

const Login = ({ setAuthenticated, setUserEmail }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Simple validation functions.
  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePassword = (password) =>
    /^(?=.*[A-Za-z])(?=.*\d)[^\s]{8,}$/.test(password);

  const handleLogin = async () => {
    const newErrors = {};
    if (!email) newErrors.email = "Email is required.";
    else if (!validateEmail(email)) newErrors.email = "Invalid email format.";
    if (!password) newErrors.password = "Password is required.";
    else if (!validatePassword(password))
      newErrors.password =
        "Password must be at least 8 characters, contain letters and digits, and have no spaces.";
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    try {
      setLoading(true);
      const response = await api.post("/auth/login", { email, password });
      const { token, user } = response.data;
      const expiry = Date.now() + 30 * 24 * 60 * 60 * 1000;
      localStorage.setItem("authToken", token);
      localStorage.setItem("authExpiry", expiry);
      localStorage.setItem("userEmail", user.email);
      setUserEmail(user.email);
      setAuthenticated(true);
      setLoading(false);
      navigate("/dashboard");
    } catch (err) {
      setLoading(false);
      setErrors({
        general: err.response?.data?.message || "Invalid email or password.",
      });
    }
  };

  // This function will be called when the user submits the form (presses Enter).
  const handleSubmit = (e) => {
    e.preventDefault();
    handleLogin();
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #003366 0%, #0077b6 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Container maxWidth="xs">
        <Paper
          component="form"
          onSubmit={handleSubmit}
          elevation={6}
          sx={{
            p: 4,
            borderRadius: 2,
            backgroundColor: "rgba(255, 255, 255, 0.9)",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <LockOutlined sx={{ fontSize: 40, color: "primary.main" }} />
          </Box>
          <Typography variant="h5" align="center" gutterBottom sx={{ fontWeight: 600 }}>
            Employee Login
          </Typography>
          {errors.general && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.general}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Email"
            margin="normal"
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!errors.email}
            helperText={errors.email}
          />
          <TextField
            fullWidth
            label="Password"
            type={passwordVisible ? "text" : "password"}
            margin="normal"
            variant="outlined"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!errors.password}
            helperText={errors.password}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setPasswordVisible(!passwordVisible)} edge="end">
                    {passwordVisible ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 3, py: 1.5, fontWeight: "bold" }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Login"}
          </Button>
          <Typography variant="body2" align="center" sx={{ mt: 2, color: "text.secondary" }}>
            Enter your credentials to access your account.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
